import { Effect } from "effect";

const FILE_SIZE_MULTIPLIER = 1024;

export const humanReadableFileSize = (sizeInBytes: number) => {
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  if (sizeInBytes === 0) {
    return "0 B";
  }

  const i = Math.floor(
    Math.log(Math.abs(sizeInBytes)) / Math.log(FILE_SIZE_MULTIPLIER)
  );

  if (i === 0) {
    return `${sizeInBytes} ${sizes[i]}`;
  }

  return `${(sizeInBytes / FILE_SIZE_MULTIPLIER ** i).toFixed(1)} ${sizes[i]}`;
};

export const readFileAsDataUrl = (file: File) =>
  Effect.runPromise(
    Effect.async<string, Error>((resume) => {
      const reader = new FileReader();
      let isSettled = false;
      const finishRef: {
        current?: (result: Effect.Effect<string, Error>) => void;
      } = {};

      const handleEvent = (event: Event) => {
        if (event.type !== "load") {
          finishRef.current?.(Effect.fail(new Error("Failed to read file.")));
          return;
        }

        if (typeof reader.result !== "string") {
          finishRef.current?.(Effect.fail(new Error("Failed to read file.")));
          return;
        }

        finishRef.current?.(Effect.succeed(reader.result));
      };

      const cleanup = () => {
        reader.removeEventListener("load", handleEvent);
        reader.removeEventListener("error", handleEvent);
        reader.removeEventListener("abort", handleEvent);
        if (reader.readyState === FileReader.LOADING) {
          reader.abort();
        }
      };

      const finish = (result: Effect.Effect<string, Error>) => {
        if (isSettled) {
          return;
        }

        isSettled = true;
        cleanup();
        resume(result);
      };
      finishRef.current = finish;

      reader.addEventListener("load", handleEvent);
      reader.addEventListener("error", handleEvent);
      reader.addEventListener("abort", handleEvent);
      reader.readAsDataURL(file);

      return Effect.sync(cleanup);
    })
  );
